/**
 * Sector Performance — the 11 sectors ranked by 1D, with ETF Performance's
 * columns (decision 6). *Equal-weighted* is each sector's own equal-weight
 * index over SP1500 (TopDown's); *ETF* is the sector's SPDR. Equal first.
 */
import { fetchBriefSectors, type SectorRow, type SectorSource, type SectorsPayload } from "../../api/daily-brief";
import { WIDGET_META } from "../../lib/brief-board";
import { Chip, MUTED } from "./cells";
import { PerfTable, type Column, type Row, type Sort } from "./PerfTable";
import { useBriefData } from "./useBriefData";
import { DataState, WidgetCard, useWidgetSetting, type WidgetProps } from "./WidgetCard";

interface SectorLine extends Row {
  rank: number;
  row: SectorRow;
}

const f = (pick: (r: SectorRow) => number | null) => (l: SectorLine) => pick(l.row);

const COLUMNS: ReadonlyArray<Column<SectorLine>> = [
  { key: "rank", label: "#", kind: "rank", value: (l) => l.rank },
  {
    key: "sector", label: "Sector", kind: "text", value: (l) => l.row.sector,
    render: (l) => (
      <>
        {l.row.sector ?? "—"} {l.row.etf && <span style={MUTED}>{l.row.etf}</span>}
      </>
    ),
  },
  { key: "fromOpen", label: "From open", kind: "bar", value: f((r) => r.fromOpen) },
  { key: "chg1", label: "1D %", kind: "bar", value: f((r) => r.chg1) },
  { key: "chg5", label: "5D %", kind: "bar", value: f((r) => r.chg5) },
  { key: "chg20", label: "20D %", kind: "bar", value: f((r) => r.chg20) },
  { key: "chg50", label: "50D %", kind: "bar", value: f((r) => r.chg50) },
  { key: "closeRange", label: "Close rng", kind: "range", value: f((r) => r.closeRange) },
];

export function SectorPerfWidget({ widget, onSettings, onRemove }: WidgetProps) {
  const [source, setSource] = useWidgetSetting<SectorSource>(widget, "source", "equal", onSettings);
  const [sort, setSort] = useWidgetSetting<Sort | null>(widget, "sort", { field: "chg1", desc: true }, onSettings);
  const state = useBriefData<SectorsPayload>(`sectors:${source}`, () => fetchBriefSectors(source));
  // The server ranks by 1D; # keeps that rank whatever the table is sorted by.
  const rows: SectorLine[] = (state.data?.rows ?? []).map((row, i) => ({
    id: row.sector ?? row.etf ?? String(i), rank: i + 1, row,
  }));

  return (
    <WidgetCard
      title={WIDGET_META[widget.type].title}
      live={state.data?.live}
      onRemove={onRemove}
      controls={
        <>
          <Chip on={source === "equal"} onClick={() => setSource("equal")}>Equal-weighted</Chip>
          <Chip on={source === "etf"} onClick={() => setSource("etf")}>ETF</Chip>
        </>
      }
    >
      <DataState state={state}>
        {() => <PerfTable columns={COLUMNS} rows={rows} sort={sort} onSort={setSort} />}
      </DataState>
    </WidgetCard>
  );
}
